import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell
} from 'recharts';
import '../styles/bpo.css';

const NAVY = '#00205b';
const GOLD = '#F1BE48';
const COR_POSICAO = '#3498db';
const COR_FECHAMENTO = '#9b59b6';

const formatarMoeda = (valor) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);

const formatarData = (valor) => {
  if (!valor) return '-';
  const d = new Date(valor);
  if (isNaN(d)) return valor;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// ── Helpers de semana (segunda-feira como início) ──
const inicioSemana = (d) => {
  const date = new Date(d);
  if (isNaN(date)) return null;
  const dia = date.getDay(); // 0=domingo .. 6=sábado
  const diff = dia === 0 ? -6 : 1 - dia;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
};
const chaveSemana = (d) => {
  const s = inicioSemana(d);
  return s ? s.toISOString().slice(0, 10) : null;
};
const rotuloSemana = (chave) => {
  if (!chave) return '';
  const [, m, dd] = chave.split('-');
  return `${dd}/${m}`;
};
const ultimasSemanas = (n) => {
  const base = inicioSemana(new Date());
  const keys = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(base);
    d.setDate(base.getDate() - i * 7);
    keys.push(d.toISOString().slice(0, 10));
  }
  return keys;
};

const GestaoBpo = () => {
  const [posicoes, setPosicoes] = useState([]);
  const [fechamentos, setFechamentos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [abaAtiva, setAbaAtiva] = useState('geral');
  const [filtroPosicao, setFiltroPosicao] = useState('');
  const [filtroFechamento, setFiltroFechamento] = useState('');

  // Filtros globais (afetam KPIs, gráficos, insights e detalhes)
  const [filtroOperador, setFiltroOperador] = useState('');
  const [dataIni, setDataIni] = useState('');
  const [dataFim, setDataFim] = useState('');

  useEffect(() => {
    const buscar = async () => {
      try {
        const token = localStorage.getItem('access');
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_BASE}/api/gestao-bpo-geral/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (data.success) {
          setPosicoes(data.posicoes || []);
          setFechamentos(data.fechamentos || []);
        } else {
          setErro('Não foi possível carregar os dados.');
        }
      } catch (e) {
        setErro('Erro ao buscar dados: ' + (e.response?.data?.error || e.message));
      } finally {
        setCarregando(false);
      }
    };
    buscar();
  }, []);

  // ── Operadores disponíveis (para o select) ──
  const operadoresDisponiveis = useMemo(() => {
    const s = new Set();
    posicoes.forEach((p) => s.add(p.operador || 'Não atribuído'));
    fechamentos.forEach((f) => s.add(f.operador || 'Não atribuído'));
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [posicoes, fechamentos]);

  // ── Aplica filtros globais (operador + período) ──
  const dentroPeriodo = (iso) => {
    if (!dataIni && !dataFim) return true;
    if (!iso) return false;
    const d = new Date(iso);
    if (isNaN(d)) return true;
    if (dataIni && d < new Date(`${dataIni}T00:00:00`)) return false;
    if (dataFim && d > new Date(`${dataFim}T23:59:59`)) return false;
    return true;
  };

  const posicoesG = useMemo(() =>
    posicoes.filter((p) =>
      (!filtroOperador || (p.operador || 'Não atribuído') === filtroOperador) &&
      dentroPeriodo(p.data_envio)
    ),
    [posicoes, filtroOperador, dataIni, dataFim]
  );

  const fechamentosG = useMemo(() =>
    fechamentos.filter((f) =>
      (!filtroOperador || (f.operador || 'Não atribuído') === filtroOperador) &&
      dentroPeriodo(f.data_envio_iso)
    ),
    [fechamentos, filtroOperador, dataIni, dataFim]
  );

  const filtrosAtivos = filtroOperador || dataIni || dataFim;
  const limparFiltros = () => { setFiltroOperador(''); setDataIni(''); setDataFim(''); };

  // ── Lista unificada de entregas ──
  const entregas = useMemo(() => {
    const arr = [];
    posicoesG.forEach((p) => {
      arr.push({
        key: 'posicao',
        tipo: 'Posição',
        data: p.data_envio ? new Date(p.data_envio) : null,
        operador: p.operador || 'Não atribuído',
        cliente: p.cliente_nome || '-',
      });
    });
    fechamentosG.forEach((f) => {
      arr.push({
        key: 'fechamento',
        tipo: 'Fechamento',
        data: f.data_envio_iso ? new Date(f.data_envio_iso) : null,
        operador: f.operador || 'Não atribuído',
        cliente: f.cliente_nome || '-',
        pendentes: f.itens_pendentes || 0,
        concluidos: f.itens_concluidos || 0,
      });
    });
    return arr;
  }, [posicoesG, fechamentosG]);

  // ── KPIs ──
  const kpis = useMemo(() => {
    const total = entregas.length;
    const limite7 = new Date();
    limite7.setDate(limite7.getDate() - 7);
    const ultimos7 = entregas.filter((e) => e.data && !isNaN(e.data) && e.data >= limite7).length;
    const operadores = new Set(entregas.map((e) => e.operador)).size;
    const clientes = new Set(entregas.map((e) => e.cliente)).size;
    const totFech = fechamentosG.length;
    const fechSemPend = fechamentosG.filter((f) => (f.itens_pendentes || 0) === 0).length;
    const pctNoPrazo = totFech ? Math.round((fechSemPend / totFech) * 100) : 0;
    const itensPendentes = fechamentosG.reduce((s, f) => s + (f.itens_pendentes || 0), 0);
    return { total, ultimos7, operadores, clientes, pctNoPrazo, itensPendentes, totFech, totPos: posicoesG.length };
  }, [entregas, fechamentosG, posicoesG]);

  // ── Entregas por semana (últimas 10) ──
  const porSemana = useMemo(() => {
    const keys = ultimasSemanas(10);
    const map = {};
    keys.forEach((k) => { map[k] = { semana: rotuloSemana(k), posicao: 0, fechamento: 0, total: 0 }; });
    entregas.forEach((e) => {
      if (!e.data || isNaN(e.data)) return;
      const k = chaveSemana(e.data);
      if (map[k]) { map[k][e.key] += 1; map[k].total += 1; }
    });
    return keys.map((k) => map[k]);
  }, [entregas]);

  // ── Entregas por operador ──
  const porOperador = useMemo(() => {
    const m = {};
    entregas.forEach((e) => {
      const o = e.operador || 'Não atribuído';
      if (!m[o]) m[o] = { operador: o, posicao: 0, fechamento: 0, total: 0, pendentes: 0 };
      m[o][e.key] += 1;
      m[o].total += 1;
      if (e.key === 'fechamento') m[o].pendentes += (e.pendentes || 0);
    });
    return Object.values(m).sort((a, b) => b.total - a.total);
  }, [entregas]);

  // ── Entregas por cliente ──
  const porCliente = useMemo(() => {
    const m = {};
    entregas.forEach((e) => {
      const c = e.cliente || '-';
      if (!m[c]) m[c] = { cliente: c, posicao: 0, fechamento: 0, total: 0, ultimaPosicao: null };
      m[c][e.key] += 1;
      m[c].total += 1;
      if (e.key === 'posicao' && e.data && !isNaN(e.data) && (!m[c].ultimaPosicao || e.data > m[c].ultimaPosicao)) {
        m[c].ultimaPosicao = e.data;
      }
    });
    return Object.values(m).sort((a, b) => b.total - a.total);
  }, [entregas]);

  // ── Heatmap operador × semana (últimas 6) ──
  const heat = useMemo(() => {
    const keys = ultimasSemanas(6);
    const ops = porOperador.map((o) => o.operador);
    const m = {};
    ops.forEach((o) => { m[o] = {}; keys.forEach((k) => { m[o][k] = 0; }); });
    entregas.forEach((e) => {
      if (!e.data || isNaN(e.data)) return;
      const k = chaveSemana(e.data);
      if (m[e.operador] && k in m[e.operador]) m[e.operador][k] += 1;
    });
    let max = 1;
    ops.forEach((o) => keys.forEach((k) => { if (m[o][k] > max) max = m[o][k]; }));
    return { keys, ops, m, max };
  }, [entregas, porOperador]);

  // ── Insights automáticos ──
  const insights = useMemo(() => {
    const out = [];
    if (porOperador.length) {
      const top = porOperador[0];
      out.push({ icon: 'fa-trophy', cor: '#27ae60', texto: `${top.operador} é quem mais entrega: ${top.total} entregas no período (${top.posicao} posições, ${top.fechamento} fechamentos).` });
      const reais = porOperador.filter((o) => o.operador !== 'Não atribuído');
      if (reais.length > 1) {
        const menos = [...reais].sort((a, b) => a.total - b.total)[0];
        out.push({ icon: 'fa-arrow-trend-down', cor: '#e67e22', texto: `${menos.operador} tem o menor volume: ${menos.total} entregas — vale checar a carga da carteira.` });
      }
    }
    const limite = new Date();
    limite.setDate(limite.getDate() - 14);
    const semPos = porCliente.filter((c) => c.posicao > 0 && (!c.ultimaPosicao || c.ultimaPosicao < limite));
    if (semPos.length) {
      out.push({ icon: 'fa-triangle-exclamation', cor: '#e74c3c', texto: `${semPos.length} cliente(s) sem posição há +14 dias: ${semPos.slice(0, 3).map((c) => c.cliente).join(', ')}${semPos.length > 3 ? '…' : ''}.` });
    }
    const fechPend = fechamentosG.filter((f) => (f.itens_pendentes || 0) > 0);
    if (fechPend.length) {
      out.push({ icon: 'fa-list-check', cor: '#e67e22', texto: `${fechPend.length} fechamento(s) enviados com pendências (${kpis.itensPendentes} itens no total).` });
    } else if (kpis.totFech > 0) {
      out.push({ icon: 'fa-circle-check', cor: '#27ae60', texto: `Todos os ${kpis.totFech} fechamentos foram enviados sem itens pendentes.` });
    }
    const naoAtrib = porOperador.find((o) => o.operador === 'Não atribuído');
    if (naoAtrib) {
      out.push({ icon: 'fa-user-slash', cor: '#7f8c8d', texto: `${naoAtrib.total} entrega(s) sem operador responsável no cadastro do cliente — atribua em Clientes para medir corretamente.` });
    }
    if (!out.length) out.push({ icon: 'fa-circle-info', cor: COR_POSICAO, texto: 'Sem dados suficientes para gerar insights.' });
    return out;
  }, [porOperador, porCliente, fechamentosG, kpis]);

  const posicoesFiltradas = posicoesG.filter((p) =>
    (p.cliente_nome || '').toLowerCase().includes(filtroPosicao.toLowerCase()) ||
    (p.operador || '').toLowerCase().includes(filtroPosicao.toLowerCase()) ||
    (p.periodo_inicio || '').includes(filtroPosicao) ||
    (p.periodo_fim || '').includes(filtroPosicao)
  );

  const fechamentosFiltrados = fechamentosG.filter((f) =>
    (f.cliente_nome || '').toLowerCase().includes(filtroFechamento.toLowerCase()) ||
    (f.mes_referencia || '').toLowerCase().includes(filtroFechamento.toLowerCase()) ||
    (f.operador || '').toLowerCase().includes(filtroFechamento.toLowerCase()) ||
    (f.enviado_por || '').toLowerCase().includes(filtroFechamento.toLowerCase())
  );

  if (carregando) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <i className="fas fa-spinner fa-spin fa-3x mb-3" style={{ color: NAVY }}></i>
          <p className="text-muted">Carregando dados BPO...</p>
        </div>
      </div>
    );
  }

  const Kpi = ({ titulo, valor, sub, icon, cor }) => (
    <div className="col-6 col-md-4 col-xl-2">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body py-3">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <p className="text-muted mb-1" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{titulo}</p>
              <h3 className="fw-bold mb-0" style={{ color: NAVY, fontVariantNumeric: 'tabular-nums' }}>{valor}</h3>
              {sub && <small className="text-muted" style={{ fontSize: '0.72rem' }}>{sub}</small>}
            </div>
            <i className={`fa-solid ${icon} fa-lg`} style={{ color: cor, opacity: 0.6 }}></i>
          </div>
        </div>
      </div>
    </div>
  );

  const Painel = ({ titulo, icon, children, right }) => (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-header bg-white d-flex justify-content-between align-items-center border-bottom">
        <span className="fw-bold" style={{ color: NAVY }}>
          {icon && <i className={`fa-solid ${icon} me-2`} style={{ color: GOLD }}></i>}
          {titulo}
        </span>
        {right}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );

  return (
    <div className="container-fluid p-4">
      {/* Cabeçalho */}
      <div className="d-flex align-items-center mb-4 gap-3">
        <div style={{ background: `linear-gradient(135deg, ${NAVY}, #0a3a8a)`, borderRadius: '12px', padding: '12px 16px' }}>
          <i className="fa-solid fa-gauge-high fa-lg" style={{ color: GOLD }}></i>
        </div>
        <div>
          <h4 className="mb-0 fw-bold" style={{ color: NAVY }}>Gestão BPO — Entregas</h4>
          <small className="text-muted">Acompanhamento das entregas (posições semanais e fechamentos mensais) por operador e cliente</small>
        </div>
      </div>

      {erro && (
        <div className="alert alert-danger"><i className="fas fa-exclamation-circle me-2"></i>{erro}</div>
      )}

      {/* Barra de filtros globais */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body py-3">
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label mb-1" style={{ fontSize: '0.78rem', fontWeight: 600, color: NAVY, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                <i className="fa-solid fa-user-tie me-2" style={{ color: GOLD }}></i>Operador
              </label>
              <select className="form-select" value={filtroOperador} onChange={(e) => setFiltroOperador(e.target.value)}>
                <option value="">Todos os operadores</option>
                {operadoresDisponiveis.map((op) => (
                  <option key={op} value={op}>{op}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3 col-6">
              <label className="form-label mb-1" style={{ fontSize: '0.78rem', fontWeight: 600, color: NAVY, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                <i className="fa-solid fa-calendar-day me-2" style={{ color: GOLD }}></i>De
              </label>
              <input type="date" className="form-control" value={dataIni} max={dataFim || undefined} onChange={(e) => setDataIni(e.target.value)} />
            </div>
            <div className="col-md-3 col-6">
              <label className="form-label mb-1" style={{ fontSize: '0.78rem', fontWeight: 600, color: NAVY, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                <i className="fa-solid fa-calendar-day me-2" style={{ color: GOLD }}></i>Até
              </label>
              <input type="date" className="form-control" value={dataFim} min={dataIni || undefined} onChange={(e) => setDataFim(e.target.value)} />
            </div>
            <div className="col-md-2">
              <button
                className="btn w-100"
                style={{ background: filtrosAtivos ? NAVY : '#e9ecef', color: filtrosAtivos ? '#fff' : '#6c757d', fontWeight: 600 }}
                onClick={limparFiltros}
                disabled={!filtrosAtivos}
              >
                <i className="fa-solid fa-eraser me-2"></i>Limpar
              </button>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
            <small className="text-muted me-1">Atalhos:</small>
            {[
              { label: '7 dias', dias: 7 },
              { label: '30 dias', dias: 30 },
              { label: '90 dias', dias: 90 },
            ].map((q) => (
              <button
                key={q.dias}
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: '0.78rem' }}
                onClick={() => {
                  const fim = new Date();
                  const ini = new Date();
                  ini.setDate(ini.getDate() - q.dias);
                  const fmt = (d) => d.toISOString().slice(0, 10);
                  setDataIni(fmt(ini));
                  setDataFim(fmt(fim));
                }}
              >
                Últimos {q.label}
              </button>
            ))}
            {filtrosAtivos && (
              <span className="badge ms-auto" style={{ background: '#eef2f8', color: NAVY, fontSize: '0.75rem' }}>
                <i className="fa-solid fa-filter me-1"></i>
                Mostrando: {entregas.length} entrega(s)
                {filtroOperador ? ` · ${filtroOperador}` : ''}
                {(dataIni || dataFim) ? ` · ${dataIni ? formatarData(dataIni) : '…'} a ${dataFim ? formatarData(dataFim) : '…'}` : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Abas */}
      <ul className="nav nav-tabs border-0 mb-4">
        <li className="nav-item">
          <button
            className={`nav-link fw-semibold ${abaAtiva === 'geral' ? 'active' : 'text-muted'}`}
            style={abaAtiva === 'geral' ? { color: NAVY, borderBottom: `3px solid ${GOLD}` } : {}}
            onClick={() => setAbaAtiva('geral')}
          >
            <i className="fa-solid fa-chart-pie me-2"></i>Visão Geral
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link fw-semibold ${abaAtiva === 'detalhes' ? 'active' : 'text-muted'}`}
            style={abaAtiva === 'detalhes' ? { color: NAVY, borderBottom: `3px solid ${GOLD}` } : {}}
            onClick={() => setAbaAtiva('detalhes')}
          >
            <i className="fa-solid fa-table-list me-2"></i>Detalhes
          </button>
        </li>
      </ul>

      {/* ════════ VISÃO GERAL ════════ */}
      {abaAtiva === 'geral' && (
        <>
          {/* KPIs */}
          <div className="row g-3 mb-4">
            <Kpi titulo="Total de entregas" valor={kpis.total} sub={`${kpis.totPos} posições · ${kpis.totFech} fechamentos`} icon="fa-box-open" cor={NAVY} />
            <Kpi titulo="Últimos 7 dias" valor={kpis.ultimos7} sub="entregas recentes" icon="fa-bolt" cor={COR_POSICAO} />
            <Kpi titulo="Operadores ativos" valor={kpis.operadores} sub="com entregas" icon="fa-user-tie" cor="#27ae60" />
            <Kpi titulo="Clientes atendidos" valor={kpis.clientes} sub="distintos" icon="fa-building" cor="#e67e22" />
            <Kpi titulo="Fechamentos no prazo" valor={`${kpis.pctNoPrazo}%`} sub="sem pendências" icon="fa-circle-check" cor="#27ae60" />
            <Kpi titulo="Itens pendentes" valor={kpis.itensPendentes} sub="nos fechamentos" icon="fa-list-check" cor="#e74c3c" />
          </div>

          {/* Insights */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderLeft: `4px solid ${GOLD}` }}>
            <div className="card-body">
              <h6 className="fw-bold mb-3" style={{ color: NAVY }}><i className="fa-solid fa-lightbulb me-2" style={{ color: GOLD }}></i>Insights</h6>
              <div className="row g-2">
                {insights.map((ins, i) => (
                  <div className="col-md-6" key={i}>
                    <div className="d-flex align-items-start gap-2 p-2 rounded" style={{ background: '#f8f9ff' }}>
                      <i className={`fa-solid ${ins.icon} mt-1`} style={{ color: ins.cor }}></i>
                      <span style={{ fontSize: '0.88rem', color: '#2b2f38' }}>{ins.texto}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Entregas por semana */}
          <div className="row g-4 mb-4">
            <div className="col-12">
              <Painel titulo="Entregas por semana (últimas 10)" icon="fa-calendar-week">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={porSemana} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="#eef0f4" />
                    <XAxis dataKey="semana" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={{ stroke: '#e7e9ee' }} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#9aa1ad' }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: 'rgba(0,32,91,0.04)' }} />
                    <Legend />
                    <Bar dataKey="posicao" name="Posições" stackId="a" fill={COR_POSICAO} radius={[0, 0, 0, 0]} maxBarSize={48} />
                    <Bar dataKey="fechamento" name="Fechamentos" stackId="a" fill={COR_FECHAMENTO} radius={[4, 4, 0, 0]} maxBarSize={48} />
                  </BarChart>
                </ResponsiveContainer>
              </Painel>
            </div>
          </div>

          {/* Por operador + por cliente */}
          <div className="row g-4 mb-4">
            <div className="col-lg-6">
              <Painel titulo="Entregas por operador" icon="fa-user-tie">
                <ResponsiveContainer width="100%" height={Math.max(220, porOperador.length * 42)}>
                  <BarChart data={porOperador} layout="vertical" margin={{ top: 5, right: 24, left: 10, bottom: 5 }}>
                    <CartesianGrid horizontal={false} stroke="#eef0f4" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: '#9aa1ad' }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="operador" width={130} tick={{ fontSize: 12, fill: '#2b2f38' }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: 'rgba(0,32,91,0.04)' }} />
                    <Legend />
                    <Bar dataKey="posicao" name="Posições" stackId="a" fill={COR_POSICAO} maxBarSize={26} />
                    <Bar dataKey="fechamento" name="Fechamentos" stackId="a" fill={COR_FECHAMENTO} radius={[0, 4, 4, 0]} maxBarSize={26} />
                  </BarChart>
                </ResponsiveContainer>
              </Painel>
            </div>
            <div className="col-lg-6">
              <Painel titulo="Top clientes por entregas" icon="fa-building">
                <ResponsiveContainer width="100%" height={Math.max(220, Math.min(porCliente.length, 12) * 42)}>
                  <BarChart data={porCliente.slice(0, 12)} layout="vertical" margin={{ top: 5, right: 24, left: 10, bottom: 5 }}>
                    <CartesianGrid horizontal={false} stroke="#eef0f4" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: '#9aa1ad' }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="cliente" width={130} tick={{ fontSize: 11, fill: '#2b2f38' }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: 'rgba(0,32,91,0.04)' }} />
                    <Legend />
                    <Bar dataKey="posicao" name="Posições" stackId="a" fill={COR_POSICAO} maxBarSize={26} />
                    <Bar dataKey="fechamento" name="Fechamentos" stackId="a" fill={COR_FECHAMENTO} radius={[0, 4, 4, 0]} maxBarSize={26} />
                  </BarChart>
                </ResponsiveContainer>
              </Painel>
            </div>
          </div>

          {/* Heatmap operador × semana */}
          <div className="row g-4">
            <div className="col-12">
              <Painel titulo="Entregas por operador × semana (últimas 6)" icon="fa-table-cells">
                {heat.ops.length === 0 ? (
                  <p className="text-muted text-center py-3 mb-0">Sem dados.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0" style={{ minWidth: '560px' }}>
                      <thead>
                        <tr>
                          <th style={{ color: NAVY, fontWeight: 700, fontSize: '0.8rem' }}>Operador</th>
                          {heat.keys.map((k) => (
                            <th key={k} className="text-center" style={{ color: NAVY, fontWeight: 700, fontSize: '0.78rem' }}>{rotuloSemana(k)}</th>
                          ))}
                          <th className="text-center" style={{ color: NAVY, fontWeight: 700, fontSize: '0.78rem' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {heat.ops.map((op) => {
                          const totalOp = heat.keys.reduce((s, k) => s + heat.m[op][k], 0);
                          return (
                            <tr key={op}>
                              <td style={{ fontSize: '0.82rem', fontWeight: 600, color: '#2b2f38' }}>{op}</td>
                              {heat.keys.map((k) => {
                                const v = heat.m[op][k];
                                const intensidade = v ? 0.12 + 0.6 * (v / heat.max) : 0;
                                return (
                                  <td key={k} className="text-center" style={{
                                    fontSize: '0.82rem',
                                    fontWeight: v ? 700 : 400,
                                    background: v ? `rgba(0,32,91,${intensidade})` : '#f8f9fa',
                                    color: intensidade > 0.45 ? '#fff' : (v ? NAVY : '#c4c9d4'),
                                    borderRadius: '4px',
                                  }}>{v || '·'}</td>
                                );
                              })}
                              <td className="text-center" style={{ fontSize: '0.82rem', fontWeight: 700, color: NAVY }}>{totalOp}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Painel>
            </div>
          </div>
        </>
      )}

      {/* ════════ DETALHES ════════ */}
      {abaAtiva === 'detalhes' && (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {/* Posições */}
            <h6 className="fw-bold mb-3" style={{ color: NAVY }}>
              <i className="fa-solid fa-chart-line me-2" style={{ color: COR_POSICAO }}></i>
              Posições Financeiras <span className="badge ms-1" style={{ background: NAVY, color: GOLD }}>{posicoes.length}</span>
            </h6>
            <div className="mb-3">
              <div className="input-group" style={{ maxWidth: '360px' }}>
                <span className="input-group-text bg-white border-end-0"><i className="fas fa-search text-muted"></i></span>
                <input type="text" className="form-control border-start-0" placeholder="Filtrar por cliente, operador ou período..." value={filtroPosicao} onChange={(e) => setFiltroPosicao(e.target.value)} />
              </div>
            </div>
            <div className="table-responsive mb-4">
              <table className="table table-hover align-middle">
                <thead>
                  <tr style={{ background: '#f8f9ff' }}>
                    {['Cliente', 'Operador', 'Período Início', 'Período Fim', 'Saldo Final', 'Total Pagar', 'Total Receber', 'Data Envio'].map((h) => (
                      <th key={h} style={{ color: NAVY, fontWeight: 700, fontSize: '0.82rem' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {posicoesFiltradas.length === 0 ? (
                    <tr><td colSpan={8} className="text-center text-muted py-4">Nenhum registro encontrado</td></tr>
                  ) : (
                    posicoesFiltradas.map((p) => (
                      <tr key={p.id}>
                        <td><span className="badge" style={{ background: NAVY, color: GOLD, fontSize: '0.8rem', padding: '5px 10px' }}>{p.cliente_nome || '-'}</span></td>
                        <td style={{ fontSize: '0.85rem' }}>{p.operador || '-'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{formatarData(p.periodo_inicio)}</td>
                        <td style={{ fontSize: '0.85rem' }}>{formatarData(p.periodo_fim)}</td>
                        <td style={{ fontSize: '0.85rem', fontWeight: 600, color: p.saldo_final >= 0 ? '#27ae60' : '#e74c3c' }}>{formatarMoeda(p.saldo_final)}</td>
                        <td style={{ fontSize: '0.85rem', color: '#e74c3c' }}>{formatarMoeda(p.total_pagar)}</td>
                        <td style={{ fontSize: '0.85rem', color: '#27ae60' }}>{formatarMoeda(p.total_receber)}</td>
                        <td style={{ fontSize: '0.8rem', color: '#888' }}>{formatarData(p.data_envio)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Fechamentos */}
            <h6 className="fw-bold mb-3" style={{ color: NAVY }}>
              <i className="fa-solid fa-calendar-check me-2" style={{ color: COR_FECHAMENTO }}></i>
              Fechamentos Mensais <span className="badge ms-1" style={{ background: NAVY, color: GOLD }}>{fechamentos.length}</span>
            </h6>
            <div className="mb-3">
              <div className="input-group" style={{ maxWidth: '360px' }}>
                <span className="input-group-text bg-white border-end-0"><i className="fas fa-search text-muted"></i></span>
                <input type="text" className="form-control border-start-0" placeholder="Filtrar por cliente, mês, operador..." value={filtroFechamento} onChange={(e) => setFiltroFechamento(e.target.value)} />
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr style={{ background: '#f8f9ff' }}>
                    {['Cliente', 'Operador', 'Mês Ref.', 'Concluídos', 'Pendentes', 'Enviado Por', 'Data Envio', 'Drive'].map((h) => (
                      <th key={h} style={{ color: NAVY, fontWeight: 700, fontSize: '0.82rem' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fechamentosFiltrados.length === 0 ? (
                    <tr><td colSpan={8} className="text-center text-muted py-4">Nenhum registro encontrado</td></tr>
                  ) : (
                    fechamentosFiltrados.map((f) => (
                      <tr key={f.id}>
                        <td><span className="badge" style={{ background: NAVY, color: GOLD, fontSize: '0.8rem', padding: '5px 10px' }}>{f.cliente_nome || '-'}</span></td>
                        <td style={{ fontSize: '0.85rem' }}>{f.operador || '-'}</td>
                        <td style={{ fontSize: '0.85rem', fontWeight: 600 }}>{f.mes_referencia || '-'}</td>
                        <td><span className="badge bg-success">{f.itens_concluidos}</span></td>
                        <td><span className={`badge ${f.itens_pendentes > 0 ? 'bg-warning text-dark' : 'bg-success'}`}>{f.itens_pendentes}</span></td>
                        <td style={{ fontSize: '0.82rem' }}>{f.enviado_por || '-'}</td>
                        <td style={{ fontSize: '0.82rem', color: '#888' }}>{f.data_envio || '-'}</td>
                        <td>
                          {f.link_pasta_drive ? (
                            <a href={f.link_pasta_drive} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary" style={{ fontSize: '0.75rem' }}>
                              <i className="fab fa-google-drive me-1"></i>Drive
                            </a>
                          ) : (
                            <span className="text-muted" style={{ fontSize: '0.8rem' }}>-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestaoBpo;
